import type { CheckboxGroupWithTextInputData } from '@ksyos/design-system/dist/components/checkbox-group-with-text-input/create-checkbox-group-options';
import { Box, Checkbox, CheckboxGroupWithTextInput, FormErrorMessage } from '@ksyos/design-system';
import { type ChangeEvent, useEffect, useState } from 'react';
import type { CheckboxOption } from '@common/validation/customControls/nestedCheckboxGroupWithTextInput';
import { getValidationErrorTranslation } from '@frontend/utils';

type CheckboxOptionWithChecked = CheckboxOption & {
    children?: CheckboxOptionWithChecked[];
    isChecked: boolean;
    textValue?: string;
};

export type NestedCheckboxGroupWithTextInputProps = {
    data: { [key: string]: boolean | string | null };
    error?: string;
    onChange: (key: string, value: boolean | string | null) => void;
    options: CheckboxOption[];
};

export function NestedCheckboxGroupWithTextInput(props: Readonly<NestedCheckboxGroupWithTextInputProps>) {
    const { data, options, onChange, error } = props;

    const [valueOptions, setValueOptions] = useState<CheckboxOptionWithChecked[]>([]);

    useEffect(() => {
        setValueOptions(addIsCheckedFromData(options, data));
    }, []);

    if (!data || options.length === 0) {
        return null;
    }

    return (
        <>
            {valueOptions.map((item) => {
                return (
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={1}
                        key={`checkbox-${item.key}`}
                        paddingBottom={2}
                    >
                        <Checkbox
                            checked={item.isChecked}
                            label={item.label}
                            onChange={(e) => {
                                onChangeCheckbox(e, item);
                            }}
                            textInput={
                                item.isTextInput ? getTextInputProps(item.label, item.key, item.textValue) : undefined
                            }
                            value={item.key}
                        />

                        {item.isChecked && item.children && item.children.length > 0 && (
                            <Box
                                display="flex"
                                flexDirection="column"
                                gap={1}
                                paddingLeft={8}
                            >
                                <CheckboxGroupWithTextInput
                                    data={item.children.reduce(
                                        (acc, subItem) => ({
                                            ...acc,
                                            [subItem.key]: {
                                                displayValue: subItem.label,
                                                hasTextInput: subItem.isTextInput,
                                                value: data[subItem.key] ?? undefined,
                                            },
                                        }),
                                        {}
                                    )}
                                    error={
                                        hasChildAnyFilledInError(error, item)
                                            ? getValidationErrorTranslation(error)
                                            : undefined
                                    }
                                    onChange={(value: CheckboxGroupWithTextInputData) => {
                                        // @TODO CS-26649 - make this possible to add a text input to a nested checkbox and to change the value in the local state
                                        Object.entries(value).forEach(([key, val]) => {
                                            onChange(key, val.value || null);
                                        });
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                );
            })}
            {error === 'object_anyFilledIn' && (
                <FormErrorMessage>{getValidationErrorTranslation(error)}</FormErrorMessage>
            )}
        </>
    );

    function onChangeCheckbox(event: ChangeEvent<HTMLInputElement>, item: CheckboxOptionWithChecked) {
        setValueOptions(updateCheckboxValue(item.key, event.target.checked, valueOptions));

        if (item.isTextInput) {
            if (!event.target.checked) {
                onChange(item.key, null);
            } else {
                onChange(item.key, item.textValue ?? '');
            }
        } else {
            onChange(item.key, event.target.checked);
            resetNestedCheckboxes(item, event.target.checked);
        }
    }

    function resetNestedCheckboxes(item: CheckboxOptionWithChecked, isChecked: boolean) {
        if (item.children && item.children.length > 0 && !isChecked) {
            item.children.forEach((subItem) => onChange(subItem.key, subItem.isTextInput ? null : false));
        }
    }

    function getUpdatedNestedCheckboxesbyParentIsChecked(
        item: CheckboxOptionWithChecked,
        isChecked: boolean
    ): CheckboxOptionWithChecked[] | undefined {
        if (!item.children || item.children.length === 0) {
            return undefined;
        }

        if (isChecked) {
            return item.children;
        }

        return item.children.map((subItem: CheckboxOptionWithChecked) => ({
            ...subItem,
            isChecked: false,
            textValue: undefined,
        }));
    }

    function updateCheckboxValue(
        key: string,
        isChecked: boolean,
        optionsArray: CheckboxOptionWithChecked[]
    ): CheckboxOptionWithChecked[] {
        return optionsArray.reduce<CheckboxOptionWithChecked[]>((acc, opt) => {
            if (opt.key === key) {
                acc.push({
                    ...opt,
                    children: getUpdatedNestedCheckboxesbyParentIsChecked(opt, isChecked),
                    isChecked,
                });
            } else if (opt.children) {
                acc.push({
                    ...opt,
                    children: updateCheckboxValue(key, isChecked, opt.children),
                });
            } else {
                acc.push(opt);
            }

            return acc;
        }, []);
    }

    function updateTextValue(
        key: string,
        value: string,
        optionsArray: CheckboxOptionWithChecked[]
    ): CheckboxOptionWithChecked[] {
        return optionsArray.reduce<CheckboxOptionWithChecked[]>((acc, opt) => {
            if (opt.key === key) {
                acc.push({ ...opt, textValue: value });
                // @TODO CS-26649 - make this possible to add a text input to a nested checkbox and to change the value in the local state
            } else {
                acc.push(opt);
            }

            return acc;
        }, []);
    }

    function addIsCheckedFromData(
        originalOptions: CheckboxOption[],
        values: { [key: string]: boolean | string | null }
    ): CheckboxOptionWithChecked[] {
        return originalOptions.map((option) => ({
            ...option,
            children: option.children ? addIsCheckedFromData(option.children, values) : undefined,
            isChecked:
                values[option.key] === true ||
                (typeof values[option.key] === 'string' && Boolean(values[option.key])) ||
                valueOptions.find((opt) => opt.key === option.key && opt.isChecked) !== undefined,

            textValue: typeof values[option.key] === 'string' ? (values[option.key] as string) : undefined,
        }));
    }

    function getTextInputProps(label: string, key: string, value: string | undefined) {
        return {
            'aria-label': label,
            onChange: (event: ChangeEvent<HTMLInputElement>) => {
                setValueOptions((prev) => updateTextValue(key, event.target.value, prev));
                onChange(key, event.target.value);
            },
            value: value ?? '',
        };
    }

    function hasChildAnyFilledInError(childError: string | undefined, item: CheckboxOption) {
        return (
            childError === 'object_childAnyFilledIn' &&
            data[item.key] === true &&
            item.children &&
            !item.children.some((subItem) => data[subItem.key] === true)
        );
    }
}
